import { Link } from "@inertiajs/react"
import { formatDistanceToNow } from "date-fns"
import {
  ArrowUpDown,
  Download,
  FileStack,
  ImageIcon,
  Lock,
  PenLine,
  Scissors,
} from "lucide-react"
import { useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { pricingPath } from "@/routes"

interface Document {
  id: number
  filename: string
  operation_type: string | null
  status: "pending" | "processing" | "completed" | "failed"
  size: number | null
  created_at: string
  expires_at: string | null
  expired: boolean
  download_url: string | null
}

interface RecentDocumentsTableProps {
  documents: Document[]
  plan: "free" | "plus" | "pro"
}

const operationIcons: Record<string, typeof FileStack> = {
  merge_pdfs: FileStack,
  split_pdf: Scissors,
  reorder_pages: ArrowUpDown,
  protect_pdf: Lock,
  sign_pdf: PenLine,
  image_to_pdf: ImageIcon,
}

const operationLabels: Record<string, string> = {
  merge_pdfs: "Merge",
  split_pdf: "Split",
  reorder_pages: "Reorder",
  protect_pdf: "Protect",
  sign_pdf: "Sign",
  image_to_pdf: "Image to PDF",
}

const statusVariants: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  pending: "secondary",
  processing: "secondary",
  completed: "default",
  failed: "destructive",
}

function formatFileSize(bytes: number | null): string {
  if (!bytes) return "-"
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function RecentDocumentsTable({
  documents,
  plan,
}: RecentDocumentsTableProps) {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const isFreeUser = plan === "free"

  if (documents.length === 0) {
    return (
      <div className="rounded-lg border p-8 text-center">
        <p className="text-muted-foreground">No documents yet.</p>
        <p className="text-muted-foreground text-sm">
          Use the quick actions above to get started.
        </p>
      </div>
    )
  }

  return (
    <>
      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>File</TableHead>
              <TableHead>Operation</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Size</TableHead>
              <TableHead className="w-16">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {documents.map((doc) => {
              const Icon = doc.operation_type
                ? operationIcons[doc.operation_type] || FileStack
                : FileStack

              const isExpired = doc.expired
              const canDownload =
                doc.status === "completed" && !isExpired && doc.download_url

              return (
                <TableRow
                  key={doc.id}
                  className={isExpired ? "opacity-50" : ""}
                >
                  <TableCell>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="block max-w-[200px] truncate">
                            {doc.filename}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>{doc.filename}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4" />
                      <span>
                        {doc.operation_type
                          ? operationLabels[doc.operation_type] ||
                            doc.operation_type
                          : "-"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        isExpired ? "outline" : statusVariants[doc.status]
                      }
                    >
                      {isExpired ? "Expired" : doc.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {formatDistanceToNow(new Date(doc.created_at), {
                      addSuffix: true,
                    })}
                  </TableCell>
                  <TableCell>{formatFileSize(doc.size)}</TableCell>
                  <TableCell>
                    {canDownload &&
                      (isFreeUser ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setShowUpgradeModal(true)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button variant="ghost" size="icon" asChild>
                          <a href={doc.download_url!} download>
                            <Download className="h-4 w-4" />
                          </a>
                        </Button>
                      ))}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upgrade to Download</DialogTitle>
            <DialogDescription>
              Download your processed documents by upgrading to a Plus or Pro
              plan. Get unlimited downloads and more features.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowUpgradeModal(false)}
            >
              Cancel
            </Button>
            <Button asChild>
              <Link href={pricingPath()}>View Plans</Link>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
